// Lógica pura del mosaico del video wall. Sin dependencias, testeable.
// Coordenadas 1-indexed (como CSS Grid: grid-column: 1 / span N).

const MAX_TILE_H = 4

export function clampSize(w, h, cols) {
  const cw = Math.max(1, Math.min(cols, Math.floor(w || 1)))
  const ch = Math.max(1, Math.min(MAX_TILE_H, Math.floor(h || 1)))
  return { w: cw, h: ch }
}

export function rectsOverlap(a, b) {
  const ax2 = a.col + a.w
  const ay2 = a.row + a.h
  const bx2 = b.col + b.w
  const by2 = b.row + b.h
  return a.col < bx2 && ax2 > b.col && a.row < by2 && ay2 > b.row
}

export function canPlace(rect, occupied, cols) {
  if (rect.col < 1 || rect.row < 1) return false
  if (rect.col + rect.w - 1 > cols) return false
  for (let i = 0; i < occupied.length; i += 1) {
    if (rectsOverlap(rect, occupied[i])) return false
  }
  return true
}

function ensureRow(occ, r, cols) {
  while (occ.length <= r) occ.push(new Array(cols).fill(false))
}

function markOccupied(occ, rect, cols) {
  for (let r = rect.row - 1; r < rect.row - 1 + rect.h; r += 1) {
    ensureRow(occ, r, cols)
    for (let c = rect.col - 1; c < rect.col - 1 + rect.w; c += 1) occ[r][c] = true
  }
}

function fitsAt(occ, col0, row0, w, h, cols) {
  if (col0 + w > cols) return false
  for (let r = row0; r < row0 + h; r += 1) {
    ensureRow(occ, r, cols)
    for (let c = col0; c < col0 + w; c += 1) if (occ[r][c]) return false
  }
  return true
}

// Scans row-major for the first slot where a w×h tile fits.
// Last-resort fallback {col:1, row:1} should never be reached in normal use
// (would only happen if >200 full rows are occupied simultaneously).
function findSlot(occ, w, h, cols) {
  let r = 0
  while (r < 200) {
    ensureRow(occ, r, cols)
    for (let c = 0; c + w <= cols; c += 1) {
      if (fitsAt(occ, c, r, w, h, cols)) return { col: c + 1, row: r + 1 }
    }
    r += 1
  }
  return { col: 1, row: 1 }
}

// { cols, tiles } → { positions: {id:{col,row,w,h}}, rows }.
// Pass 1: anchor pinned tiles in list order; reject overlapping ones → demote to floaters.
// Pass 2: non-pinned tiles + demoted pinneds, placed in list order via findSlot.
export function packMosaic({ cols, tiles }) {
  const c = Math.max(1, Math.floor(cols || 4))
  const list = Array.isArray(tiles) ? tiles : []
  const occ = []
  const positions = {}
  const floatFallback = []

  list.forEach((t) => {
    if (!t || !t.pinned) return
    const size = clampSize(t.w, t.h, c)
    const col = Math.max(1, Math.min(c - size.w + 1, Math.floor(t.pinned.col)))
    const row = Math.max(1, Math.floor(t.pinned.row))
    const rect = {
      col,
      row,
      w: size.w,
      h: size.h,
    }
    if (canPlace(rect, Object.values(positions), c)) {
      positions[t.id] = rect
      markOccupied(occ, rect, c)
    } else {
      floatFallback.push(t)
    }
  })

  const demotedIds = new Set(floatFallback.map((t) => t.id))
  list.forEach((t) => {
    if (!t) return
    if (t.pinned && !demotedIds.has(t.id)) return
    const size = clampSize(t.w, t.h, c)
    const slot = findSlot(occ, size.w, size.h, c)
    const rect = {
      col: slot.col,
      row: slot.row,
      w: size.w,
      h: size.h,
    }
    positions[t.id] = rect
    markOccupied(occ, rect, c)
  })

  let rows = 0
  Object.keys(positions).forEach((id) => {
    const p = positions[id]
    rows = Math.max(rows, p.row + p.h - 1)
  })
  return { positions, rows: Math.max(1, rows) }
}

export function reconcileTiles(cameras, tiles) {
  const ids = (cameras || []).map((c) => c.id)
  const idSet = new Set(ids)
  const tileList = tiles || []
  const out = []
  const seen = new Set()
  tileList.forEach((t) => {
    if (t && idSet.has(t.id) && !seen.has(t.id)) {
      out.push(t)
      seen.add(t.id)
    }
  })
  ids.forEach((id) => {
    if (!seen.has(id)) {
      out.push({ id, w: 1, h: 1 })
      seen.add(id)
    }
  })
  return out
}

// row has no upper clamp intentionally: the grid allows unlimited vertical scroll.
export function pointToCell(x, y, geom) {
  const stepX = geom.cellW + geom.gap
  const stepY = geom.cellH + geom.gap
  let col = Math.floor(x / stepX) + 1
  let row = Math.floor(y / stepY) + 1
  if (col < 1) col = 1
  if (col > geom.cols) col = geom.cols
  if (row < 1) row = 1
  return { col, row }
}

const LEGACY_LAYOUT_COLS = {
  '1x1': 4, '1x4': 4, '1x8': 8, '1x12': 12, '2x4': 4, '2x8': 8, '2x12': 12,
}

// Columnas válidas del selector. Cualquier valor fuera de aquí (migración vieja
// como '1x1'→1, o un preset corrupto) se normaliza a 4 para no romper el toggle.
export const VALID_COLS = [2, 3, 4, 6, 8, 12]
export function normalizeCols(c) {
  return VALID_COLS.includes(c) ? c : 4
}

// Migra el layout del tray: usa el grid persistido si es válido; si no, deriva
// { cols, tiles } del layout string legacy + las cámaras (como 1x1).
export function migrateTrayGrid(trayState, persistedGrid) {
  if (persistedGrid && typeof persistedGrid === 'object' && persistedGrid.cols) {
    return { ...persistedGrid, cols: normalizeCols(persistedGrid.cols) }
  }
  const cols = normalizeCols(LEGACY_LAYOUT_COLS[trayState && trayState.layout] || 4)
  const cameras = (trayState && trayState.cameras) || []
  return { cols, tiles: reconcileTiles(cameras, []) }
}

// grid.tiles + mapa {id: cameraObj} → tiles de preset.
// Guarda una COPIA del objeto de cámara del tray (id, name, streamUrl, isPtz,
// uniqueId, devId). Crítico: `isPtz` y el `streamUrl` correcto de sub-cámaras se
// calculan al fijar (toTrayCamera decodifica attributes.devN); reconstruir desde
// el monitor crudo los perdía. Guardar el objeto completo los preserva.
// Tiles sin cámara conocida en camById se filtran.
export function buildPresetTiles(gridTiles, camById) {
  return (gridTiles || []).reduce((acc, t) => {
    const cam = camById[t.id]
    if (!cam) return acc
    const out = {
      camera: {
        id: cam.id,
        name: cam.name,
        streamUrl: cam.streamUrl,
        isPtz: !!cam.isPtz,
        uniqueId: cam.uniqueId,
        devId: cam.devId,
      },
      w: t.w || 1,
      h: t.h || 1,
    }
    if (t.pinned) out.pinned = { col: t.pinned.col, row: t.pinned.row }
    acc.push(out)
    return acc
  }, [])
}

// preset → { cameras, grid }. Usa el objeto de cámara guardado tal cual (preserva
// isPtz/streamUrl como cualquier cámara fijada persistida). `resolver` es opcional
// y solo se usa para presets en formato viejo (cameraRef) por compatibilidad.
export function resolvePresetToTray(preset, resolver) {
  const cameras = []
  const tiles = []
  const presetTiles = preset.tiles || []
  presetTiles.forEach((t, idx) => {
    let cam
    if (t.camera && t.camera.id != null) {
      // Formato nuevo: objeto de cámara completo guardado en el preset.
      cam = { ...t.camera }
    } else if (t.cameraRef) {
      // Formato viejo (solo identidad): intenta resolver contra monitors vivos.
      const live = resolver ? resolver(t.cameraRef) : null
      cam = live || {
        id: 'missing-' + (t.cameraRef.id || 'x') + '-' + idx,
        devId: t.cameraRef.devId,
        name: 'No disponible',
        streamUrl: '',
        isPtz: false,
        _missing: true,
      }
    } else {
      cam = {
        id: 'missing-' + idx, name: 'No disponible', streamUrl: '', isPtz: false, _missing: true,
      }
    }
    cameras.push(cam)
    const tile = { id: cam.id, w: t.w || 1, h: t.h || 1 }
    if (t.pinned) tile.pinned = { col: t.pinned.col, row: t.pinned.row }
    tiles.push(tile)
  })
  return { cameras, grid: { cols: normalizeCols(preset.cols), tiles } }
}
